// Declarative specs for the Engineering Insights dashboard. Each spec maps a
// stored metricKey (see the impl spec metric-key table) to one chart on the
// page, replicating the original Mongo Charts dashboard layout.
// This module is intentionally dependency-free so root vitest tests can
// import it directly (same precedent as app/src/lib/format.ts).

export interface EngineeringChartTarget {
	/** Marker value in the chart's DISPLAY unit (i.e. after valueScale). */
	value: number;
	/** Value is per-week; monthly granularity scales it by 52/12. */
	perWeek?: boolean;
	/** Short label rendered on the reference line. */
	label: string;
}

export interface EngineeringChartSpec {
	id: string;
	title: string;
	description: string;
	metricKey: string;
	chartType: "line" | "bar" | "stackedBar" | "horizontalBar";
	series: "squad" | "engineer" | "segmentValue" | "none";
	/** Fold all periods into per-series totals (for yearly/total charts). */
	totals?: boolean;
	/** AI charts intentionally include team/lt rows that are hidden from delivery charts. */
	squadFilter?: boolean;
	fullWidth?: boolean;
	topNSeries?: number;
	showOverlays?: boolean;
	/**
	 * Operational metrics have no work_item/PR drill-down mapping; false
	 * disables the point-click "Show related data points" menu.
	 */
	relatedDocuments?: boolean;
	valueLabel: string;
	/** Tooltip value suffix ("%" and "$" attach tight; words get a space). */
	unit?: string;
	/** Multiply raw row values for display (e.g. 0.001 for ms → seconds). */
	valueScale?: number;
	/** Collapse per-service segment series into one per-period mean. */
	combineSegmentsMean?: boolean;
	/** Keep only rows for this segmentValue (e.g. aihr-spa operational cards). */
	segmentValue?: string;
	/** Industry benchmark marker; only set where a citable number exists. */
	target?: EngineeringChartTarget;
	section:
		| "Flow"
		| "DORA"
		| "Quality & Review"
		| "Per-Engineer"
		| "AI Productivity"
		| "AI Cost (estimated)"
		| "AI Adoption"
		| "Operational Health";
}

export const ENGINEERING_CHART_SECTIONS = [
	"Flow",
	"DORA",
	"Quality & Review",
	"Per-Engineer",
	"AI Productivity",
	"AI Cost (estimated)",
	"AI Adoption",
	"Operational Health"
] as const;

export const ENGINEERING_CHART_SPECS: EngineeringChartSpec[] = [
	// Flow
	{
		id: "work-items-throughput",
		title: "Work items throughput",
		description: "Completed work items, split by squad.",
		metricKey: "throughput",
		chartType: "line",
		series: "squad",
		valueLabel: "Work items",
		unit: "items",
		section: "Flow"
	},
	{
		id: "story-points-throughput",
		title: "Story points throughput",
		description: "Story points delivered, split by squad.",
		metricKey: "story_points_throughput",
		chartType: "line",
		series: "squad",
		valueLabel: "Story points",
		unit: "pts",
		section: "Flow"
	},
	{
		id: "cycle-time",
		title: "Cycle time",
		description: "Average hours from work start to completion, by squad.",
		metricKey: "cycle_time_hours",
		chartType: "line",
		series: "squad",
		valueLabel: "Hours",
		unit: "h",
		// LinearB community benchmarks: "good" cycle-time ceiling is 72h. Their
		// cycle time is PR-scoped; ours is work-item start→completion, so treat
		// the line as an aspirational floor.
		target: { value: 72, label: "Industry good: <72h" },
		section: "Flow"
	},
	{
		id: "completed-by-type",
		title: "Completed by type",
		description: "Completed work items, stacked by issue type.",
		metricKey: "completed_work_items_by_type",
		chartType: "stackedBar",
		series: "segmentValue",
		valueLabel: "Work items",
		unit: "items",
		section: "Flow"
	},
	{
		id: "bottleneck-stages",
		title: "Bottleneck stages",
		description: "Average hours spent per workflow stage, stacked by stage.",
		metricKey: "bottleneck_stage_time_hours",
		chartType: "stackedBar",
		series: "segmentValue",
		valueLabel: "Hours",
		unit: "h",
		section: "Flow"
	},
	// DORA
	{
		id: "deployment-frequency",
		title: "Deployment frequency",
		description: "Deployments, split by squad.",
		metricKey: "deployment_frequency",
		chartType: "line",
		series: "squad",
		valueLabel: "Deployments",
		unit: "deploys",
		// DORA 2024: "high" performers deploy between once a day and once a week.
		target: { value: 1, perWeek: true, label: "DORA high: \u22651/week" },
		section: "DORA"
	},
	{
		id: "lead-time-for-changes",
		title: "Lead time for changes",
		description: "Average hours from first commit to production, by squad.",
		metricKey: "lead_time_for_changes_hours",
		chartType: "line",
		series: "squad",
		valueLabel: "Hours",
		unit: "h",
		// DORA 2024: "high" performers land changes in under one week (168h).
		target: { value: 168, label: "DORA high: <1 week" },
		section: "DORA"
	},
	{
		id: "change-failure",
		title: "Change failure rate",
		description: "Share of deployments causing a failure (proxy).",
		metricKey: "change_failure_proxy",
		chartType: "line",
		series: "squad",
		valueLabel: "Percent",
		unit: "%",
		// DORA 2024 cluster values: elite 5% / high 10% / medium 15%; 15% is the
		// commonly cited industry average.
		target: { value: 15, label: "Industry avg: 15%" },
		section: "DORA"
	},
	// Quality & Review
	{
		id: "qa-dwell-time",
		title: "QA dwell time",
		description: "Average hours work items spend in QA, by squad.",
		metricKey: "qa_dwell_time_hours",
		chartType: "line",
		series: "squad",
		valueLabel: "Hours",
		unit: "h",
		section: "Quality & Review"
	},
	{
		id: "pr-review-turnaround",
		title: "PR review turnaround",
		description: "Average minutes to first pull request review, by squad.",
		metricKey: "pr_review_turnaround_minutes",
		chartType: "line",
		series: "squad",
		valueLabel: "Minutes",
		unit: "min",
		// Google eng-practices guidance: respond to reviews within one business
		// day (chart unit is minutes).
		target: { value: 1440, label: "Industry: <1 day" },
		section: "Quality & Review"
	},
	{
		id: "pr-review-depth",
		title: "PR review depth",
		description: "Average review comments per pull request, by squad.",
		metricKey: "pr_review_depth",
		chartType: "line",
		series: "squad",
		valueLabel: "Comments per PR",
		unit: "comments",
		section: "Quality & Review"
	},
	// Per-Engineer
	{
		id: "engineer-completed-work-items",
		title: "Completed work items by engineer",
		description: "Completed work items, split by engineer.",
		metricKey: "engineer_completed_work_items",
		chartType: "bar",
		series: "engineer",
		fullWidth: true,
		valueLabel: "Work items",
		unit: "items",
		section: "Per-Engineer"
	},
	{
		id: "engineer-story-points",
		title: "Story points by engineer",
		description: "Story points delivered, split by engineer.",
		metricKey: "engineer_story_points_delivered",
		chartType: "bar",
		series: "engineer",
		fullWidth: true,
		valueLabel: "Story points",
		unit: "pts",
		section: "Per-Engineer"
	},
	{
		id: "points-delivered-total",
		title: "Points delivered — total",
		description: "Total story points delivered per engineer over the selected range.",
		metricKey: "engineer_story_points_delivered",
		chartType: "horizontalBar",
		series: "engineer",
		totals: true,
		fullWidth: true,
		valueLabel: "Story points",
		unit: "pts",
		section: "Per-Engineer"
	},
	{
		id: "qa-failed-by-engineer-total",
		title: "QA-failed tickets — total",
		description: "Total tickets that failed QA per engineer over the selected range.",
		metricKey: "engineer_qa_failed_tickets",
		chartType: "horizontalBar",
		series: "engineer",
		totals: true,
		fullWidth: true,
		valueLabel: "Tickets",
		unit: "tickets",
		section: "Per-Engineer"
	},
	{
		id: "qa-fail-attempts-weekly",
		title: "QA failure attempts",
		description: "QA failure attempts, split by engineer.",
		metricKey: "engineer_qa_failure_attempts",
		chartType: "bar",
		series: "engineer",
		fullWidth: true,
		valueLabel: "Attempts",
		unit: "attempts",
		section: "Per-Engineer"
	},
	{
		id: "engineer-hotfixes",
		title: "Hotfixes — total",
		description: "Total hotfixes shipped per engineer over the selected range.",
		metricKey: "engineer_hotfixes",
		chartType: "horizontalBar",
		series: "engineer",
		totals: true,
		fullWidth: true,
		valueLabel: "Hotfixes",
		unit: "hotfixes",
		section: "Per-Engineer"
	},
	// AI Productivity
	{
		id: "ai-vs-non-ai-lines",
		title: "AI vs non-AI committed lines",
		description: "Committed added lines split by AI-assisted vs non-AI code.",
		metricKey: "ai_commit_lines",
		chartType: "stackedBar",
		series: "segmentValue",
		squadFilter: false,
		valueLabel: "Lines",
		unit: "lines",
		section: "AI Productivity"
	},
	{
		id: "ai-code-share",
		title: "AI share of committed code",
		description: "Percent of committed added lines attributed to AI.",
		metricKey: "ai_code_share_pct",
		chartType: "line",
		series: "none",
		squadFilter: false,
		showOverlays: true,
		valueLabel: "Percent",
		unit: "%",
		section: "AI Productivity"
	},
	{
		id: "ai-acceptance-rate",
		title: "Suggestion acceptance rate",
		description: "Accepted Cursor lines divided by suggested Cursor lines.",
		metricKey: "ai_acceptance_rate_pct",
		chartType: "line",
		series: "none",
		squadFilter: false,
		showOverlays: true,
		valueLabel: "Percent",
		unit: "%",
		section: "AI Productivity"
	},
	// AI Cost
	{
		id: "ai-estimated-cost-by-model",
		title: "Estimated AI cost by model",
		description: "Estimated weekly token cost, split by model.",
		metricKey: "ai_est_cost_usd",
		chartType: "stackedBar",
		series: "segmentValue",
		squadFilter: false,
		fullWidth: true,
		topNSeries: 10,
		valueLabel: "USD",
		unit: "$",
		section: "AI Cost (estimated)"
	},
	{
		id: "ai-estimated-cost-by-engineer",
		title: "Estimated AI cost by engineer",
		description: "Estimated token cost by engineer over the selected range.",
		metricKey: "engineer_ai_est_cost_usd",
		chartType: "horizontalBar",
		series: "engineer",
		totals: true,
		squadFilter: false,
		valueLabel: "USD",
		unit: "$",
		section: "AI Cost (estimated)"
	},
	{
		id: "ai-cost-per-line",
		title: "Estimated cost per AI line",
		description: "Estimated token cost divided by AI-committed lines.",
		metricKey: "ai_cost_per_ai_line_usd",
		chartType: "line",
		series: "none",
		squadFilter: false,
		showOverlays: true,
		valueLabel: "USD / line",
		unit: "$",
		section: "AI Cost (estimated)"
	},
	// AI Adoption
	{
		id: "ai-active-users",
		title: "Weekly active AI users",
		description: "Weekly active Cursor users as a share of the 9-seat baseline.",
		metricKey: "ai_active_users_pct",
		chartType: "line",
		series: "none",
		squadFilter: false,
		showOverlays: true,
		valueLabel: "Percent",
		unit: "%",
		// Stack Overflow Developer Survey 2025: 51% of professional developers
		// use AI tools daily — a conservative floor for weekly actives.
		target: { value: 51, label: "Devs using AI daily: 51%" },
		section: "AI Adoption"
	},
	{
		id: "ai-lines-by-engineer",
		title: "AI lines by engineer",
		description: "Total Cursor AI lines per engineer from the leaderboard export.",
		metricKey: "engineer_ai_lines",
		chartType: "horizontalBar",
		series: "engineer",
		totals: true,
		squadFilter: false,
		valueLabel: "Lines",
		unit: "lines",
		section: "AI Adoption"
	},
	// Operational Health (Datadog app health + incidents, GitHub Actions CI).
	// Rows carry squad "engineering", so squadFilter is off everywhere; the
	// per-service/per-repo split comes from segmentValue series.
	{
		id: "ops-app-error-rate",
		title: "App error rate",
		description:
			"Production aihr-spa JS source errors (Datadog RUM Errors: env:prod @error.source:source) per session.",
		metricKey: "dd_app_error_rate_pct",
		chartType: "line",
		series: "none",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Percent",
		unit: "%",
		segmentValue: "aihr-spa",
		// Common service-level convention: keep the 5xx error share below 1%.
		target: { value: 1, label: "Common SLO: <1%" },
		section: "Operational Health"
	},
	{
		id: "ops-api-latency-p95",
		title: "API latency p95",
		description:
			"95th percentile aihr-spa fetch/XHR resource duration from Datadog RUM.",
		metricKey: "dd_app_latency_p95_ms",
		chartType: "line",
		series: "none",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Seconds",
		unit: "s",
		segmentValue: "aihr-spa",
		valueScale: 0.001,
		section: "Operational Health"
	},
	{
		id: "ops-spa-lcp-p75",
		title: "aihr-spa LCP p75",
		description: "75th percentile Largest Contentful Paint for aihr-spa (Core Web Vitals).",
		metricKey: "dd_spa_lcp_p75_ms",
		chartType: "line",
		series: "none",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Seconds",
		unit: "s",
		valueScale: 0.001,
		// web.dev Core Web Vitals: LCP p75 \u22642.5s rates "good".
		target: { value: 2.5, label: "Core Web Vitals good: \u22642.5s" },
		section: "Operational Health"
	},
	{
		id: "ops-app-availability",
		title: "Availability",
		description:
			"Share of healthy 5-minute aihr-spa windows (JS source errors vs views).",
		metricKey: "dd_app_availability_pct",
		chartType: "line",
		series: "none",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Percent",
		unit: "%",
		segmentValue: "aihr-spa",
		// "Three nines" — the standard availability SLO baseline.
		target: { value: 99.9, label: "Three nines: 99.9%" },
		section: "Operational Health"
	},
	{
		id: "ops-ci-success-rate",
		title: "CI success rate",
		description: "Successful GitHub Actions runs on the default branch, per repo.",
		metricKey: "ci_success_rate_pct",
		chartType: "line",
		series: "segmentValue",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Percent",
		unit: "%",
		// CircleCI State of Software Delivery recommended default-branch
		// success benchmark.
		target: { value: 90, label: "CI benchmark: 90%" },
		section: "Operational Health"
	},
	{
		id: "ops-ci-duration-p95",
		title: "CI duration p95",
		description: "95th percentile GitHub Actions run duration, per repo.",
		metricKey: "ci_run_duration_p95_min",
		chartType: "line",
		series: "segmentValue",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Minutes",
		unit: "min",
		section: "Operational Health"
	},
	{
		id: "ops-incidents-opened",
		title: "Incidents opened",
		description: "Datadog incidents declared, stacked by severity.",
		metricKey: "dd_incidents_opened",
		chartType: "stackedBar",
		series: "segmentValue",
		squadFilter: false,
		relatedDocuments: false,
		fullWidth: true,
		valueLabel: "Incidents",
		unit: "incidents",
		section: "Operational Health"
	},
	{
		id: "ops-incident-mttr",
		title: "Incident MTTR",
		description: "Mean time to resolve Datadog incidents resolved in the period.",
		metricKey: "dd_incident_mttr_hours",
		chartType: "line",
		series: "none",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Hours",
		unit: "h",
		// DORA 2024: "high" performers recover from failed deployments in under
		// a day.
		target: { value: 24, label: "DORA high: <1 day" },
		section: "Operational Health"
	},
	{
		id: "ops-incidents-open",
		title: "Open incidents",
		description: "Datadog incidents still open at the end of each period.",
		metricKey: "dd_incidents_open",
		chartType: "line",
		series: "none",
		squadFilter: false,
		relatedDocuments: false,
		valueLabel: "Incidents",
		unit: "incidents",
		section: "Operational Health"
	}
];
